import { Page, TextField, Select, Modal } from '../components/forms';
import { useAuth } from '../App';
import { useTransferCommands } from '../components/inventory/useTransferCommands';
import { useEffect, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  FormLayout,
  Banner,
  Pagination,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface StockLevel {
  id: string;
  product_id: string;
  location_id: number;
  sku: string;
  title: string;
  location_code: string;
  warehouse_name: string;
  quantity: number;
  allocated: number;
  available: number;
  reorder_point: number;
}

export default function Inventory() {
  const { user, currentShop, currentOrganization } = useAuth();
  return <InventoryPage key={`${user?.id}-${currentShop?.id}-${currentOrganization?.id}`} />;
}
function InventoryPage() {
  const transfers = useTransferCommands();
  const { fetchApi, error } = useApi();
  const [stockLevels, setStockLevels] = useState<StockLevel[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [warehouseFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');

  // Modals
  const [adjustModalActive, setAdjustModalActive] = useState(false);
  const [selectedStock, setSelectedStock] = useState<StockLevel | null>(null);

  // Adjust form
  const [adjustQuantity, setAdjustQuantity] = useState('');
  const [adjustType, setAdjustType] = useState('set');
  const [adjustReason, setAdjustReason] = useState('');

  useEffect(() => {
    loadStockLevels();
  }, [page, warehouseFilter, stockFilter]);

  async function loadStockLevels() {
    const params = new URLSearchParams({
      page: String(page),
      limit: '50',
    });

    if (warehouseFilter !== 'all') {
      params.set('warehouse', warehouseFilter);
    }
    if (stockFilter === 'low') {
      params.set('low_stock', 'true');
    } else if (stockFilter === 'zero') {
      params.set('zero_stock', 'true');
    }
    if (searchQuery) {
      params.set('search', searchQuery);
    }

    const data = await fetchApi<{ stock: StockLevel[]; total: number }>(`/inventory/stock?${params.toString()}`);
    if (data) {
      setStockLevels(data.stock || []);
      setTotalCount(data.total || 0);
    }
  }

  const handleSearch = () => {
    setPage(1);
    loadStockLevels();
  };

  const openAdjustModal = (stock: StockLevel) => {
    setSelectedStock(stock);
    setAdjustQuantity(String(stock.quantity));
    setAdjustType('set');
    setAdjustReason('');
    setAdjustModalActive(true);
  };

  const handleAdjust = async () => {
    if (!selectedStock) return;

    let newQuantity = parseInt(adjustQuantity, 10);
    if (adjustType === 'add') {
      newQuantity = selectedStock.quantity + newQuantity;
    } else if (adjustType === 'remove') {
      newQuantity = selectedStock.quantity - newQuantity;
    }

    await fetchApi(`/inventory/stock/${selectedStock.id}/adjust`, {
      method: 'POST',
      body: JSON.stringify({
        quantity: newQuantity,
        reason: adjustReason || 'Manual adjustment',
      }),
    });

    setAdjustModalActive(false);
    loadStockLevels();
  };

  function getAdjustmentLabel(type: string): string {
    switch (type) {
      case 'set':
        return 'New Quantity';
      case 'add':
        return 'Quantity to Add';
      case 'remove':
        return 'Quantity to Remove';
      default:
        return 'Quantity';
    }
  }


  function stockBadge(stock: StockLevel): JSX.Element {
    if (stock.available <= 0) {
      return <Badge tone="critical">Out of Stock</Badge>;
    }
    if (stock.available <= stock.reorder_point) {
      return <Badge tone="warning">Low Stock</Badge>;
    }
    return <Badge tone="success">In Stock</Badge>;
  }

  return (
    <Page
      title="Inventory"
      primaryAction={{
        content: 'Stock In/Out',
        url: '/inventory-operations',
      }}
      secondaryActions={[
        {content: 'Move stock between bins', url: '/bin-moves'},
        {content: 'Set up stock tracking', url: '/stock-enrollment'},
        {content: 'Record opening stock', url: '/opening-stock'},
        {
          content: 'Stock Take',
          url: '/stock-takes',
        },
        {
          content: 'Import Stock',
          onAction: () => {}, // TODO: Implement CSV import
        },
        {
          content: 'Publish stock to Shopify',
          url: '/shopify-updates',
        },
      ]}
    >
      <Layout>
        {(transfers.state.pending || transfers.state.error || transfers.state.blocked) && <Layout.Section>
          <Banner tone="warning" title="Stock transfer">
            <p>{transfers.state.error || 'A saved transfer needs confirmation. Retry it before making another transfer.'}</p>
            {transfers.state.pending && <Button loading={transfers.state.busy} onClick={async () => { await transfers.retry(); if (transfers.snapshot().success) loadStockLevels(); }}>Retry saved transfer</Button>}
          </Banner>
        </Layout.Section>}
        {transfers.state.success && <Layout.Section><Banner tone="success">Stock transfer saved.</Banner></Layout.Section>}
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" blockAlign="end">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Search"
                  labelHidden
                  placeholder="Search by SKU or product name..."
                  value={searchQuery}
                  onChange={setSearchQuery}
                  autoComplete="off"
                  connectedRight={
                    <Button onClick={handleSearch}>Search</Button>
                  }
                />
              </div>
              <Select
                label="Stock Level"
                labelHidden
                options={[
                  { label: 'All Stock', value: 'all' },
                  { label: 'Low Stock', value: 'low' },
                  { label: 'Out of Stock', value: 'zero' },
                ]}
                value={stockFilter}
                onChange={(value) => {
                  setStockFilter(value);
                  setPage(1);
                }}
              />
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Stock Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              {stockLevels.length > 0 ? (
                <>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'text']}
                    headings={['SKU', 'Product', 'Location', 'Qty', 'Allocated', 'Available', 'Status', 'Actions']}
                    rows={stockLevels.map((stock) => [
                      stock.sku,
                      stock.title.length > 30 ? stock.title.substring(0, 30) + '...' : stock.title,
                      `${stock.warehouse_name} - ${stock.location_code}`,
                      stock.quantity.toString(),
                      stock.allocated.toString(),
                      stock.available.toString(),
                      stockBadge(stock),
                      <InlineStack gap="200">
                        <Button size="slim" url={'/stock-enrollment/'+stock.product_id}>Tracking setup</Button>
                        <Button size="slim" onClick={() => openAdjustModal(stock)}>
                          Adjust
                        </Button>
                        <Button size="slim" url="/bin-moves">
                          Transfer
                        </Button>
                      </InlineStack>,
                    ])}
                  />

                  <InlineStack align="center">
                    <Pagination
                      hasPrevious={page > 1}
                      hasNext={page * 50 < totalCount}
                      onPrevious={() => setPage(page - 1)}
                      onNext={() => setPage(page + 1)}
                    />
                    <Text as="span" tone="subdued">
                      {totalCount} items
                    </Text>
                  </InlineStack>
                </>
              ) : (
                <BlockStack gap="400" inlineAlign="center">
                  <Text as="h3" variant="headingMd">No stock levels found</Text>
                  <Text as="p" tone="subdued" alignment="center">
                    Stock levels track inventory at specific warehouse locations.
                    To add stock, first create a warehouse and locations, then receive goods.
                  </Text>
                  <InlineStack gap="300">
                    <Button url="/warehouses">Set Up Warehouses</Button>
                    <Button url="/products" variant="plain">View Products</Button>
                  </InlineStack>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Adjust Stock Modal */}
      <Modal
        open={adjustModalActive}
        onClose={() => setAdjustModalActive(false)}
        title={`Adjust Stock - ${selectedStock?.sku || ''}`}
        primaryAction={{
          content: 'Apply Adjustment',
          onAction: handleAdjust,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setAdjustModalActive(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Text as="p">
              Current quantity: <strong>{selectedStock?.quantity}</strong>
            </Text>

            <Select
              label="Adjustment Type"
              options={[
                { label: 'Set to exact quantity', value: 'set' },
                { label: 'Add quantity', value: 'add' },
                { label: 'Remove quantity', value: 'remove' },
              ]}
              value={adjustType}
              onChange={setAdjustType}
            />

            <TextField
              label={getAdjustmentLabel(adjustType)}
              type="number"
              requiredIndicator value={adjustQuantity}
              onChange={setAdjustQuantity}
              autoComplete="off"
            />

            <TextField
              label="Reason"
              requiredIndicator value={adjustReason}
              onChange={setAdjustReason}
              placeholder="e.g., Damaged goods, Stock count correction"
              autoComplete="off"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

    </Page>
  );
}
